import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { uuidv7 } from "uuidv7";
import { api, ApiError } from "../api";
import type { AuthState } from "../auth/AuthProvider";
import { consentTextsQueryOptions, meQueryOptions } from "../queries";
import { logEvent } from "../sentry";
import { Spinner } from "../login/Spinner";

function RegisterRoute() {
  return <RegisterScreen auth={Route.useRouteContext().auth} />;
}

export const Route = createFileRoute("/register")({
  component: RegisterRoute,
});

// Exported for tests (rendered without a router).
export function RegisterScreen({ auth }: { auth: AuthState }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const texts = useQuery(consentTextsQueryOptions());
  const [error, setError] = useState<string | null>(null);
  // One ClientCommandId per submit attempt, reused if THIS submit is retried.
  const commandId = useRef(uuidv7());

  const register = useMutation({
    mutationFn: (version: string) =>
      api.register(auth, { clientCommandId: commandId.current, acceptedConsentVersion: version }),
    onSuccess: async (account) => {
      logEvent("register.completed");
      // Write-through: the gate's ["me", uid] query is never observed by this screen,
      // so invalidating alone doesn't refetch it — the stale pre-registration 404 (cached
      // as null by resolveAuthedGate) would otherwise survive the navigate below and
      // bounce us straight back to /register. Seed the cache with what we just got back.
      queryClient.setQueryData(meQueryOptions(auth).queryKey, account);
      await navigate({ to: "/home" });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.detail : "You appear to be offline — check your connection and try again.");
      commandId.current = uuidv7(); // a NEW attempt is a new command
    },
  });

  if (texts.isLoading) return <main className="min-h-screen grid place-items-center text-slate-400">Loading…</main>;
  if (texts.isError || !texts.data) {
    return (
      <main className="min-h-screen grid place-items-center p-6 text-center text-slate-500">
        <div>
          <p>Something went wrong loading the consent terms.</p>
          <button onClick={() => texts.refetch()} className="mt-3 text-teal-600 underline">Try again</button>
        </div>
      </main>
    );
  }

  const platform = texts.data.platform;
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-6 py-8 shadow-sm space-y-4">
        <h1 className="text-xl font-semibold text-slate-800">One last step</h1>
        <p className="whitespace-pre-wrap text-sm text-slate-600">{platform.text}</p>
        <p className="text-xs text-slate-400">Consent version: {platform.version}</p>
        {error && (
          <div className="text-sm text-rose-600">
            <p>{error}</p>
            <p className="mt-1 text-slate-500">If you think this is a mistake, please contact the carwash.</p>
          </div>
        )}
        <button
          onClick={() => register.mutate(platform.version)}
          disabled={register.isPending}
          className="w-full rounded-xl bg-teal-600 px-4 py-3 font-semibold text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {register.isPending && <Spinner size={18} />}
          Accept &amp; continue
        </button>
      </div>
    </main>
  );
}
